/**
 * Number-plate normalisation and shape validation, mirroring
 * UserVehicleService.ValidateAndNormaliseRegistration on the API.
 *
 * The point of checking here is to spend nothing on a plate that cannot be real: an
 * unchecked value costs a request to our API, a DVSA token fetch and a DVSA lookup before
 * coming back as "no record", which reads as a data problem rather than the typo it is.
 *
 * Deliberately a SHAPE check, not the DVLA plate grammar. UK plates span current-style
 * (AB12CDE), prefix and suffix styles, Northern Ireland (ABC1234), dateless cherished
 * marks (A1, VIP1) and Q plates, so encoding the full grammar here would reject valid
 * plates the DVSA would have resolved. Every real plate does have 2-10 alphanumerics with
 * at least one letter and at least one digit, which is enough to catch the junk.
 *
 * The server re-checks regardless - this only saves the round trip.
 */

export const MIN_REGISTRATION_LENGTH = 2;

/** Matches UserVehicles.Registration VARCHAR(10). */
export const MAX_REGISTRATION_LENGTH = 10;

/** Upper-cases and strips everything that is not a letter or a digit, as DVSA indexes them. */
export function normaliseRegistration(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Returns null when the plate could be real, or a message to show the user when it cannot.
 */
export function validateRegistration(input: string): string | null {
  const plate = normaliseRegistration(input);

  if (plate.length === 0) return 'Enter a vehicle registration.';

  if (plate.length < MIN_REGISTRATION_LENGTH || plate.length > MAX_REGISTRATION_LENGTH) {
    return `A number plate is between ${MIN_REGISTRATION_LENGTH} and ${MAX_REGISTRATION_LENGTH} letters and numbers.`;
  }

  if (!/[A-Z]/.test(plate) || !/[0-9]/.test(plate)) {
    return 'A number plate contains both letters and numbers.';
  }

  return null;
}
